import moment from 'moment';
import { isDecimalNeeded } from 'shared/utils/strUtil';
import DevExpSearchAndVisibilityTable from 'shared/components/tables/DevExpSearchAndVisibilityTable';
import React, { useMemo } from 'react';
import NoDataFoundComponent from 'shared/components/NoDataFoundComponent';
import { useUserSettingsContext } from 'users/utils/contexts/UserSettingsContext';
import {
  getCellDisplayValue,
  POD_DETAILS_TABLE_COLUMNS,
  POD_DETAILS_TABLE_COLUMNS_EXTENSIONS,
  POD_DETAILS_TABLE_COLUMNS_WIDTHS,
} from './PodDetails.constants.tsx';
import styles from './PodDetailsView.module.scss';
import useTable from 'shared/hooks/customHooks/useTable';

type Props = {
  data: never[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  podGeneralData: any;
};

const PodDetailsView: React.FC<Props> = ({ data, podGeneralData }) => {
  const { numStrAbriviaionByDisplayMetric } = useUserSettingsContext();

  const sortedData = useMemo(() => {
    const parameterOrder = {
      cpu: 1,
      memory: 2,
      network_tx: 3,
      network_rx: 4,
      storage: 5,
    };
    return data?.sort((a: { parameter: string }, b: { parameter: string }) => {
      return (parameterOrder[a.parameter] || 0) - (parameterOrder[b.parameter] || 0);
    });
  }, [data]);

  const generalDataTotalCost = podGeneralData ? parseFloat(podGeneralData.totalCost) : 0;
  const { NewTableWrapper } = useTable();

  const renderPodDetailsTable = () => {
    if (!sortedData || !sortedData.length) {
      return <NoDataFoundComponent height="455px" isBorderNeeded={false} />;
    }
    const columns = POD_DETAILS_TABLE_COLUMNS.map((column) => {
      if (column.formatNumValue) {
        const originalGetCellValue = column.getCellValue || ((row) => row[column.name]);
        column.getCellValue = (row) => {
          const value = originalGetCellValue(row);
          if (value !== undefined) {
            return value;
          }
          return getCellDisplayValue(row, column.name, numStrAbriviaionByDisplayMetric);
        };
      }
      return column;
    });

    return (
      <NewTableWrapper className={styles.table}>
        <DevExpSearchAndVisibilityTable
          data={sortedData}
          hideFilters
          isDownloadAsCsv={false}
          columns={columns}
          costColumnsArray={['cost', 'costWaste', 'totalCost']}
          currenciesColumns={['cost', 'costWaste', 'totalCost']}
          tableColumnExtensions={POD_DETAILS_TABLE_COLUMNS_EXTENSIONS}
          columnWidths={POD_DETAILS_TABLE_COLUMNS_WIDTHS}
          defaultSorting={[]}
          isResize
          isVisibility={false}
          numCostDecimal={3}
        />
      </NewTableWrapper>
    );
  };

  if (!podGeneralData) {
    return null;
  }
  return (
    <div className={styles.container}>
      <div className={styles.generalInfo}>
        <div>
          <span>Pod Name:</span>
          <span>{podGeneralData.podName || ''}</span>
        </div>
        <div>
          <span>Date:</span>
          <span>{moment(podGeneralData.usageDate).format('YYYY-MM-DD')}</span>
        </div>
        <div>
          <span>Instance Type:</span>
          <span>{podGeneralData.instanceType || ''}</span>
        </div>
        {podGeneralData.namespaceName && (
          <div>
            <span>Namespace Name:</span>
            <span>{podGeneralData.namespaceName || ''}</span>
          </div>
        )}
        <div>
          <span>Resource Id:</span>
          <span>{podGeneralData.resourceId || ''}</span>
        </div>
        <div>
          <span>Total Pod Cost:</span>
          <span>${(generalDataTotalCost || 0).toFixed(isDecimalNeeded(generalDataTotalCost || 0, 3) ? 3 : 0)}</span>
        </div>
        <div>
          <span>Total Running Hours:</span>
          <span>
            {podGeneralData.runningHours > 0 && podGeneralData.runningHours < 1
              ? 'Less then 1 hour'
              : (podGeneralData.runningHours || 0).toFixed(
                  isDecimalNeeded((podGeneralData || {}).runningHours || 0, 3) ? 3 : 0,
                )}
          </span>
        </div>
      </div>
      {renderPodDetailsTable()}
    </div>
  );
};

export default PodDetailsView;
